<template>
  <div class="map-container">
    <div v-if="!latitude || !longitude" class="no-location">
      <p>No location data available</p>
    </div>
    <div v-else>
      <l-map
        :zoom="zoom"
        :center="center"
        :options="mapOptions"
        style="height: 400px; width: 100%"
        @ready="onMapReady"
      >
        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>

        <!-- Marker untuk lokasi POD -->
        <l-marker :lat-lng="center" :icon="customIcon">
          <l-popup>
            <div>
              <strong>POD Location</strong><br />
              Latitude: {{ latitude }}<br />
              Longitude: {{ longitude }}<br />
              <span v-if="address">{{ address }}</span>
            </div>
          </l-popup>
        </l-marker>

        <!-- Circle untuk menunjukkan radius akurasi jika ada -->
        <l-circle
          v-if="accuracy"
          :lat-lng="center"
          :radius="accuracy"
          :color="'red'"
          :fill-color="'red'"
          :fill-opacity="0.1"
        >
          <l-popup> Accuracy radius: {{ accuracy }}m </l-popup>
        </l-circle>

        <!-- Info Panel with Customer Location -->
        <l-control
          v-if="customerLocation && customerLocation.latitude"
          position="topright"
        >
          <div class="location-info bg-white p-3 rounded shadow-lg border">
            <h5 class="font-bold text-gray-800 mb-2">
              📊 Location Information
            </h5>
            <div class="text-sm space-y-1">
              <p>
                <span class="font-medium">📍 Lat Customer:</span>
                {{ customerLocation.latitude || "N/A" }}
              </p>
              <p>
                <span class="font-medium">📍 Long Customer:</span>
                {{ customerLocation.longitude || "N/A" }}
              </p>
              <p>
                <span class="font-medium">🎯 Lat Delivery:</span>
                {{ latitude }}
              </p>
              <p>
                <span class="font-medium">🎯 Long Delivery:</span>
                {{ longitude }}
              </p>
              <p v-if="distanceBetweenCustomerAndVisit">
                <span class="font-medium">📏 Distance:</span>
                {{ distanceBetweenCustomerAndVisit }}
              </p>
              <p v-if="timestamp">
                <span class="font-medium">⏱️ Timestamp:</span>
                {{ formatTimestamp(timestamp) }}
              </p>
            </div>
          </div>
        </l-control>
      </l-map>

      <!-- Action buttons -->
      <div class="map-actions mt-3">
        <vs-button
          @click="openInGoogleMaps"
          size="small"
          color="primary"
          icon-pack="feather"
          icon="icon-external-link"
        >
          Open in Google Maps
        </vs-button>
        <vs-button
          @click="centerMap"
          size="small"
          color="success"
          icon-pack="feather"
          icon="icon-target"
          class="ml-2"
        >
          Center Map
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LCircle,
  LControl,
} from "vue2-leaflet";

export default {
  name: "MapViewer",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LCircle,
    LControl,
  },
  props: {
    latitude: {
      type: [Number, String],
      default: null,
    },
    longitude: {
      type: [Number, String],
      default: null,
    },
    accuracy: {
      type: [Number, String],
      default: null,
    },
    timestamp: {
      type: String,
      default: null,
    },
    address: {
      type: String,
      default: null,
    },
    height: {
      type: String,
      default: "400px",
    },
    customerLocation: {
      type: Object,
      default: null,
    },
    distanceBetweenCustomerAndVisit: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      zoom: 15,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      mapOptions: {
        zoomSnap: 0.5,
        zoomControl: true,
        attributionControl: true,
      },
      map: null,
    };
  },
  computed: {
    center() {
      if (this.latitude && this.longitude) {
        return [parseFloat(this.latitude), parseFloat(this.longitude)];
      }
      return [-6.2088, 106.8456]; // Default Jakarta coordinates
    },
    customIcon() {
      // Custom icon untuk marker POD
      return this.$L.divIcon({
        html: `<div style="background-color: #ff4757; color: white; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">📍</div>`,
        className: "custom-div-icon",
        iconSize: [30, 30],
        iconAnchor: [15, 30],
      });
    },
  },
  methods: {
    onMapReady(map) {
      this.map = map;
      // Set view ke lokasi POD jika ada
      if (this.latitude && this.longitude) {
        map.setView(this.center, this.zoom);
      }
    },
    centerMap() {
      if (this.map && this.latitude && this.longitude) {
        this.map.setView(this.center, this.zoom);
      }
    },
    openInGoogleMaps() {
      if (this.latitude && this.longitude) {
        const url = `https://maps.google.com/maps?q=${this.latitude},${this.longitude}&z=${this.zoom}`;
        window.open(url, "_blank");
      }
    },
    formatTimestamp(timestamp) {
      return moment(timestamp).format("DD/MM/YYYY HH:mm:ss");
    },
  },
  watch: {
    latitude: {
      handler() {
        this.$nextTick(() => {
          if (this.map && this.latitude && this.longitude) {
            this.map.setView(this.center, this.zoom);
          }
        });
      },
    },
    longitude: {
      handler() {
        this.$nextTick(() => {
          if (this.map && this.latitude && this.longitude) {
            this.map.setView(this.center, this.zoom);
          }
        });
      },
    },
  },
};
</script>

<style scoped>
.map-container {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #fff;
}

.no-location {
  text-align: center;
  padding: 40px;
  color: #666;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.map-info {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
}

.map-actions {
  display: flex;
  gap: 8px;
}

/* Custom leaflet styles */
.custom-div-icon {
  background: transparent;
  border: none;
}
</style>
